import type { CoachingPartnership, Resource } from '@efdigital/ll-addon-data';
import { debounce, titleCase } from '@efdigital/ll-addon-ui';
import ResourceThumbnail from '@efdigital/ll-xceed-v2/components/coaching/resource-thumbnail.gts';
import config from '@efdigital/ll-xceed-v2/config/environment';
import { asAsyncBelongsTo } from '@efdigital/ll-xceed-v2/data/async-setter';
import {
  createCoachingSharedResource,
  isAlreadySharedError,
  queryCatalogResources,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-shared-resources';
import {
  type CoachingSharedResource,
  DataTypeCoachingSharedResource,
} from '@efdigital/ll-xceed-v2/data/types/coaching-shared-resource';
import { leadershipLiveResourceUrl } from '@efdigital/ll-xceed-v2/utils/coaching/leadership-live-resource-url';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { withReactiveResponse } from '@warp-drive/core/request';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

const LLIVE_ORIGIN = config['ll-apps'].llive;
/** Same page size as the admin resource picker (3-column card grid). */
const PAGE_SIZE = 30;

interface ShareResourceModalSignature {
  Args: {
    partnership: CoachingPartnership | null;
    sharedResourceIds: readonly string[];
    onShared: () => void | Promise<void>;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

function eq(left: unknown, right: unknown): boolean {
  return left === right;
}

function not(value: boolean): boolean {
  return !value;
}

function openUrl(resource: Resource): string | null {
  return leadershipLiveResourceUrl(LLIVE_ORIGIN, resource);
}

export default class ShareResourceModal extends Component<ShareResourceModalSignature> {
  @service declare store: Store;

  @tracked open = false;
  @tracked searchText = '';
  @tracked pageIndex = 0;
  @tracked note = '';
  @tracked sharingId: string | null = null;
  @tracked extraSharedIds: string[] = [];
  @tracked errorMessage: string | null = null;

  get offset(): number {
    return this.pageIndex * PAGE_SIZE;
  }

  get catalogQuery() {
    return withReactiveResponse<Resource[]>(
      queryCatalogResources(this.searchText, {
        offset: this.offset,
        limit: PAGE_SIZE,
      })
    );
  }

  isShared = (resource: Resource): boolean => {
    const id = resource.id;
    if (!id) {
      return false;
    }
    return (
      this.args.sharedResourceIds.includes(id) ||
      this.extraSharedIds.includes(id)
    );
  };

  isSharing = (resource: Resource): boolean => this.sharingId === resource.id;

  showMore = (count: number): boolean => count >= PAGE_SIZE;

  showPager = (count: number): boolean =>
    this.pageIndex > 0 || this.showMore(count);

  showModal = () => {
    this.searchText = '';
    this.pageIndex = 0;
    this.note = '';
    this.errorMessage = null;
    this.extraSharedIds = [];
    this.open = true;
  };

  closeModal = () => {
    this.open = false;
  };

  setSearchText = (event: Event) => {
    this.searchText = (event.target as HTMLInputElement).value;
    this.pageIndex = 0;
  };

  setNote = (event: Event) => {
    this.note = (event.target as HTMLTextAreaElement).value;
  };

  previousPage = () => {
    if (this.pageIndex > 0) {
      this.pageIndex -= 1;
    }
  };

  nextPage = () => {
    this.pageIndex += 1;
  };

  share = async (resource: Resource) => {
    const partnership = this.args.partnership;
    if (
      this.sharingId ||
      !resource.id ||
      !partnership ||
      this.isShared(resource)
    ) {
      return;
    }

    this.sharingId = resource.id;
    this.errorMessage = null;

    const created = this.store.createRecord<CoachingSharedResource>(
      DataTypeCoachingSharedResource,
      {
        coachingPartnership: asAsyncBelongsTo(partnership),
        resource: asAsyncBelongsTo(resource),
        note: this.note.trim(),
      }
    );

    try {
      await this.store.request(
        createCoachingSharedResource(created, this.store)
      );
      this.extraSharedIds = [...this.extraSharedIds, resource.id];
      await this.args.onShared();
    } catch (error) {
      created.unloadRecord();
      if (isAlreadySharedError(error)) {
        this.extraSharedIds = [...this.extraSharedIds, resource.id];
        this.errorMessage = 'You have already shared that item.';
        await this.args.onShared();
      } else {
        console.error(error);
        this.errorMessage = 'That item could not be shared. Please try again.';
      }
    } finally {
      this.sharingId = null;
    }
  };

  <template>
    {{yield this.showModal}}

    {{#if this.open}}
      <BsModal
        @open={{this.open}}
        @onHidden={{this.closeModal}}
        @size="xl"
        @renderInPlace={{true}}
        as |modal|
      >
        <modal.header>
          <h2 class="modal-title h5">Share from the library</h2>
        </modal.header>
        <modal.body>
          <p class="text-body-secondary">
            Find resources for your coachee.
          </p>

          <div class="mb-3">
            <label for="share-resource-search" class="form-label">Search</label>
            <input
              id="share-resource-search"
              type="search"
              class="form-control"
              placeholder="Search by title or keyword"
              value={{this.searchText}}
              {{debounce this.setSearchText}}
              data-test-share-resource-search
            />
          </div>

          <div class="mb-3">
            <label for="share-resource-note" class="form-label">Note
              <span
                class="text-body-secondary fw-normal"
              >(optional)</span></label>
            <textarea
              id="share-resource-note"
              class="form-control"
              rows="2"
              maxlength="2000"
              placeholder="Why you are sending this — added to the next item you share"
              value={{this.note}}
              {{on "input" this.setNote}}
            ></textarea>
          </div>

          {{#if this.errorMessage}}
            <p class="text-danger small" role="alert">{{this.errorMessage}}</p>
          {{/if}}

          <Request @query={{this.catalogQuery}}>
            <:content as |response|>
              {{#if response.data.length}}
                <div class="row my-4">
                  {{#each response.data as |resource|}}
                    <div class="col-md-4 pb-4">
                      <div
                        class="card h-100 border shadow-sm rounded-3 overflow-hidden"
                        data-test-catalog-resource
                      >
                        <ResourceThumbnail @resource={{resource}} />
                        <div class="card-body">
                          <h3 class="h6 card-title fw-bold">
                            {{titleCase resource.title}}
                          </h3>
                          {{#if resource.abstract}}
                            <p
                              class="card-text small text-body-secondary mb-0 text-truncate"
                            >
                              {{resource.abstract}}
                            </p>
                          {{/if}}
                        </div>
                        <div
                          class="card-footer bg-transparent border-0 pt-0 d-flex flex-wrap gap-2"
                        >
                          {{#let (openUrl resource) as |url|}}
                            {{#if url}}
                              <a
                                href={{url}}
                                class="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Preview
                                <iconify-icon
                                  icon="ph:arrow-square-out"
                                  inline
                                  aria-hidden="true"
                                />
                                <span class="visually-hidden">
                                  (opens in a new tab)
                                </span>
                              </a>
                            {{/if}}
                          {{/let}}
                          {{#if (this.isShared resource)}}
                            <button
                              type="button"
                              class="btn btn-outline-primary btn-sm"
                              disabled
                            >Shared</button>
                          {{else}}
                            <button
                              type="button"
                              class="btn btn-primary btn-sm"
                              disabled={{this.isSharing resource}}
                              data-test-share-resource
                              {{on "click" (fn this.share resource)}}
                            >{{if
                                (eq this.sharingId resource.id)
                                "Sharing…"
                                "Share"
                              }}</button>
                          {{/if}}
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>

                {{#if (this.showPager response.data.length)}}
                  <div
                    class="d-flex justify-content-between align-items-center gap-2 mt-3"
                  >
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      disabled={{eq this.pageIndex 0}}
                      {{on "click" this.previousPage}}
                    >Previous</button>
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      disabled={{not (this.showMore response.data.length)}}
                      {{on "click" this.nextPage}}
                    >Next</button>
                  </div>
                {{/if}}
              {{else}}
                <div
                  class="alert alert-light border mb-0 d-flex align-items-center gap-2"
                  role="status"
                >
                  <iconify-icon inline icon="ph:info" aria-hidden="true" />
                  <span>No matches. Try a different search.</span>
                </div>
              {{/if}}
            </:content>
            <:loading>
              <div class="text-center my-4">
                <div class="spinner-grow" role="status">
                  <span class="visually-hidden">Loading the library…</span>
                </div>
              </div>
            </:loading>
            <:error>
              <div class="alert alert-danger mb-0" role="alert">
                The library could not be loaded. Please try again.
              </div>
            </:error>
          </Request>
        </modal.body>
        <modal.footer>
          <button
            type="button"
            class="btn btn-outline-primary"
            {{on "click" this.closeModal}}
          >Close</button>
        </modal.footer>
      </BsModal>
    {{/if}}
  </template>
}
